import {
  parseJuneauWire,
  type AiAdapterInput,
  type AiBackendAdapter,
  type AiStreamEvent
} from 'juneau';
import { apolloClient } from '@/services/apollo-client';
import { ChatStreamDocument } from '@/graphql';
import i18n from '@/i18n/config';
import { takeArmedNudge } from './nudges/armed-nudge';

/**
 * The Tappeer UI language, read at send time. The provider context carries it too, but a
 * greeting fires on mount before that state update lands — and a greeting has no user text
 * to infer a language from, so a stale value there means an English greeting.
 * resolvedLanguage is the normalized code ('cs'), while language can hold 'cs-CZ'.
 */
const currentLanguage = (): string => i18n.resolvedLanguage ?? i18n.language ?? 'en';

export { armNudge } from './nudges/armed-nudge';

/**
 * Juneau's sendGreeting sends the hint as a `system` turn, but the backend accepts only
 * user/assistant roles — the greeting instruction is server-owned by design. So a system
 * turn is translated into GREETING mode and its text is dropped: the server builds the
 * briefing itself from the caller's own data.
 */
export function toStreamVariables(input: AiAdapterInput): {
  messages: Array<{ id: string; role: string; parts: unknown; createdAt?: string | null }>;
  mode: string;
  nudgeId?: string;
} {
  const isGreeting = input.messages.some(message => message.role === 'system');
  // A greeting never carries an offer; an armed one waits for the user's own message.
  const nudgeId = isGreeting ? null : takeArmedNudge();

  return {
    ...(nudgeId !== null && { nudgeId }),
    // Pick only the fields ChatStreamInput accepts (drops `v` and any extras).
    messages: input.messages
      .filter(message => message.role !== 'system')
      .map(m => ({
        id: m.id,
        role: m.role,
        parts: m.parts,
        createdAt: m.createdAt instanceof Date ? m.createdAt.toISOString() : m.createdAt
      })),
    mode: isGreeting ? 'GREETING' : nudgeId !== null ? 'NUDGE' : 'CHAT'
  };
}

/** Bridges an Apollo subscription observable into a pull-based async iterable. */
function subscribeAsIterable(input: AiAdapterInput): AsyncIterable<unknown> {
  return {
    [Symbol.asyncIterator]() {
      const queue: unknown[] = [];
      let notify: (() => void) | null = null;
      let done = false;
      let failure: unknown;

      const wake = () => {
        notify?.();
        notify = null;
      };

      const subscription = apolloClient
        .subscribe({
          query: ChatStreamDocument,
          variables: {
            input: {
              ...toStreamVariables(input),
              // Language last: both sources come from i18n, so the one read now is the
              // freshest — the provider context can still hold the pre-mount value.
              context: { ...(input.context ?? {}), language: currentLanguage() }
            }
          }
        })
        .subscribe({
          next: result => {
            if (result.data?.chatStream !== undefined && result.data?.chatStream !== null) {
              queue.push(result.data.chatStream);
            }
            wake();
          },
          error: err => {
            failure = err;
            done = true;
            wake();
          },
          complete: () => {
            done = true;
            wake();
          }
        });

      return {
        async next(): Promise<IteratorResult<unknown>> {
          for (;;) {
            if (queue.length > 0) {
              return { value: queue.shift(), done: false };
            }
            if (failure !== undefined) {
              throw failure;
            }
            if (done) {
              return { value: undefined, done: true };
            }
            await new Promise<void>(resolve => {
              notify = resolve;
            });
          }
        },
        async return(): Promise<IteratorResult<unknown>> {
          // Consumer stopped (user cancelled/unmounted) — unsubscribing closes the WS
          // operation, which aborts the model stream server-side.
          subscription.unsubscribe();
          done = true;
          return { value: undefined, done: true };
        }
      };
    }
  };
}

/**
 * Juneau backend adapter over the chatStream GraphQL subscription (graphql-ws).
 * Replaces the former SSE adapter — auth rides on the WS session cookie.
 */
export function createGraphqlWsAdapter(): AiBackendAdapter {
  return {
    async *sendMessage(input: AiAdapterInput): AsyncIterable<AiStreamEvent> {
      for await (const payload of subscribeAsIterable(input)) {
        // parseJuneauWire owns the wire→stream-event mapping (never throws);
        // it takes a raw JSON string, so re-serialize the already-parsed payload.
        yield* parseJuneauWire(JSON.stringify(payload));
      }
    }
  };
}
